import styled, { keyframes } from 'styled-components';

import { Text } from '@/nova/components';

import { ANIM, COLORS } from '../../../../theme/tokens';

// Wide labeled provider pill: 48px tall (the app control size), fully rounded, subtle icon-button
// fill + hairline border, brand glyph followed by a visible label. Grows to share the row equally
// (flex:1) so two pills sit side by side. Sizes are structural px (allowed); colours and timings
// come from tokens.
export const Root = styled.button<{ $loading: boolean }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex: 1 1 0;
  min-width: 0;
  /* Tight icon/label gap + padding so the longer label ("Sign in with Telegram") fits on one line
     two-up inside the 480px auth card (measured: the longer label overflowed by ~3px at 8px/14px). */
  gap: 6px;
  /* height sets the desktop (row) size; min-height keeps the 48px control floor when the bar
     flips to a column on mobile, where flex:1 1 0 would otherwise collapse the main-axis height
     to the label's content height. */
  height: 48px;
  min-height: 48px;
  padding: 0 12px;
  border: 1px solid ${COLORS.featureCardBorder};
  border-radius: 99px;
  background: ${COLORS.surfaceMuted};
  color: ${COLORS.text};
  font-size: 14px;
  font-weight: 600;
  white-space: nowrap;
  cursor: pointer;
  transition: opacity ${ANIM.INTERACT_MS}ms ease;

  &:disabled {
    cursor: default;
    opacity: ${({ $loading }) => ($loading ? '1' : '0.5')};
  }
`;

// The visible provider label. Truncates rather than wrapping so the pill keeps its single-line pill
// shape if a translation runs long. `Text` renders a <span> (inline) and Label is a flex item of
// Root, so `display: block` + `min-width: 0` are both needed for text-overflow:ellipsis to fire
// (an inline span won't ellipsis, and a flex item's default min-width:auto blocks it from shrinking
// below its content width). Root already sets white-space:nowrap.
export const Label = styled(Text)`
  display: block;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
`;

export const IconWrap = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  /* Drives the 1em glyph to ~20px. */
  font-size: 20px;
  line-height: 0;
`;

const spin = keyframes`
  to {
    transform: rotate(360deg);
  }
`;

// Geometry/transform-only spinner: the ring uses currentColor (COLORS.text), so no colour literal.
export const Spinner = styled.span`
  display: inline-block;
  width: 1em;
  height: 1em;
  border: 2px solid currentColor;
  border-top-color: transparent;
  border-radius: 50%;
  animation: ${spin} ${ANIM.CREATIVE_OVERLAY_FADE_MS}ms linear infinite;

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`;
